"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "@/components/atlas-link";
import { ExternalLink, LoaderCircle } from "lucide-react";
import type { NotesFeedItem } from "@/lib/notes-feed";
import { cn } from "@/lib/utils";

// The /api/notes/feed HTTP contract: { items, nextCursor } (see
// documentation/site_architecture/routes-and-ui.md#pagination), not the raw
// Convex-shaped NotesFeedPage used for the server-rendered first page.
type NotesFeedListPage = {
  items: NotesFeedItem[];
  nextCursor: string | null;
};

function formatFeedDate(timestamp: number) {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? "—" : date.toISOString().slice(0, 10);
}

function internalNoteHref(slug: string, filterQuery: string) {
  const params = new URLSearchParams({ note: slug });
  for (const [key, value] of new URLSearchParams(filterQuery)) {
    params.append(key, value);
  }
  return `/notes?${params.toString()}`;
}

function FeedRow({
  item,
  filterQuery,
  selected,
}: {
  item: NotesFeedItem;
  filterQuery: string;
  selected: boolean;
}) {
  const className = cn(
    "block border-l-2 px-4 py-3 transition-colors duration-150",
    "atlas-v2:grid atlas-v2:min-w-0 atlas-v2:grid-cols-[minmax(0,1fr)_auto] atlas-v2:gap-2 atlas-v2:focus-visible:outline-2 atlas-v2:focus-visible:-outline-offset-2 atlas-v2:focus-visible:outline-ring",
    selected
      ? "border-primary bg-muted/60 atlas-v2:bg-sidebar-accent"
      : "border-transparent hover:bg-muted/40 atlas-v2:hover:bg-sidebar-accent/60",
  );
  const content = (
    <>
      <span className="flex items-baseline justify-between gap-3 atlas-v2:contents">
        <span
          className={cn(
            "truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-primary/90",
            "atlas-v2:col-span-full atlas-v2:row-start-1 atlas-v2:min-w-0 atlas-v2:overflow-visible atlas-v2:text-xs atlas-v2:leading-[1.5] atlas-v2:tracking-[0.033em] atlas-v2:whitespace-normal atlas-v2:[overflow-wrap:anywhere]",
            item.external && "atlas-v2:pr-5",
          )}
        >
          {item.sourceName}
        </span>
        <time
          className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground atlas-v2:col-start-2 atlas-v2:row-start-3 atlas-v2:self-center atlas-v2:text-xs atlas-v2:leading-[1.5]"
          dateTime={new Date(item.publishedAt).toISOString()}
        >
          {formatFeedDate(item.publishedAt)}
        </time>
      </span>
      <span className="mt-1 line-clamp-2 text-sm font-medium leading-snug text-foreground atlas-v2:col-span-full atlas-v2:row-start-2 atlas-v2:mt-0 atlas-v2:line-clamp-none atlas-v2:min-w-0 atlas-v2:leading-[1.5] atlas-v2:[overflow-wrap:anywhere]">
        {item.title}
      </span>
      <span className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground atlas-v2:contents">
        <span className="truncate atlas-v2:col-start-1 atlas-v2:row-start-3 atlas-v2:min-w-0 atlas-v2:self-center atlas-v2:overflow-visible atlas-v2:leading-[1.5] atlas-v2:whitespace-normal atlas-v2:[overflow-wrap:anywhere]">
          {item.author || item.sourceName}
        </span>
        {item.external ? (
          <ExternalLink
            aria-hidden
            className="size-3.5 shrink-0 atlas-v2:col-start-2 atlas-v2:row-start-1 atlas-v2:size-3 atlas-v2:self-center atlas-v2:justify-self-end"
          />
        ) : null}
      </span>
    </>
  );

  return (
    <li className="border-b border-border/40 last:border-b-0">
      {item.external ? (
        <a
          className={className}
          href={item.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          {content}
        </a>
      ) : item.slug ? (
        <Link
          aria-current={selected ? "page" : undefined}
          className={className}
          href={internalNoteHref(item.slug, filterQuery)}
        >
          {content}
        </Link>
      ) : (
        <div className={className}>{content}</div>
      )}
    </li>
  );
}

export function NotesFeedList({
  filterQuery,
  initialCursor,
  initialDone,
  initialItems,
  selectedSlug,
}: {
  filterQuery: string;
  initialCursor: string;
  initialDone: boolean;
  initialItems: NotesFeedItem[];
  selectedSlug?: string;
}) {
  const [items, setItems] = useState(initialItems);
  const [cursor, setCursor] = useState(initialCursor);
  const [done, setDone] = useState(initialDone);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const sentinelRef = useRef<HTMLLIElement | null>(null);

  const loadMore = useCallback(
    async (retry = false) => {
      if (done || loading || (failed && !retry) || !cursor) return;
      if (retry) setFailed(false);
      setLoading(true);
      try {
        const params = new URLSearchParams(filterQuery);
        params.set("cursor", cursor);
        const response = await fetch(`/api/notes/feed?${params.toString()}`, {
          cache: "no-store",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (!response.ok)
          throw new Error(`Feed request returned ${response.status}.`);
        const page = (await response.json()) as NotesFeedListPage;
        setItems((current) => {
          const seen = new Set(current.map((item) => item.stableKey));
          return [
            ...current,
            ...page.items.filter((item) => !seen.has(item.stableKey)),
          ];
        });
        setCursor(page.nextCursor ?? "");
        setDone(page.nextCursor === null);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [cursor, done, failed, filterQuery, loading],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || done || failed) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadMore();
        }
      },
      { rootMargin: "320px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [done, failed, loadMore]);

  return (
    <ol className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain atlas-v2:min-w-0">
      {items.map((item) => (
        <FeedRow
          item={item}
          key={item.stableKey}
          filterQuery={filterQuery}
          selected={
            !item.external && Boolean(item.slug) && item.slug === selectedSlug
          }
        />
      ))}
      {!done ? (
        <li
          className="flex min-h-16 items-center justify-center px-4 py-3 text-xs text-muted-foreground"
          ref={sentinelRef}
        >
          {failed ? (
            <button
              className="font-medium text-primary hover:underline"
              onClick={() => {
                void loadMore(true);
              }}
              type="button"
            >
              Try loading more results again
            </button>
          ) : (
            <span className="inline-flex items-center gap-2">
              <LoaderCircle
                aria-hidden
                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
              />
              Loading more results
            </span>
          )}
        </li>
      ) : items.length > 0 ? (
        <li className="px-4 py-4 text-center text-[11px] text-muted-foreground">
          You&apos;ve reached the end of the results.
        </li>
      ) : (
        <li
          className="px-4 py-4 text-center text-sm text-muted-foreground"
          role="status"
        >
          No articles match this search.
        </li>
      )}
    </ol>
  );
}
